import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  FiCheckCircle,
  FiEdit2,
  FiEye,
  FiFilter,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import { useNavigate } from "react-router-dom";
import axios from "../../utills/api";

const APPLICATION_STATUSES = [
  "Submitted",
  "Under Review",
  "Approved",
  "Rejected",
  "Withdrawn",
  "Admitted",
];

const Applications = () => {
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get("/applications");

      const data = response?.data?.data;

      if (Array.isArray(data)) {
        setApplications(data);
      } else if (Array.isArray(data?.applications)) {
        setApplications(data.applications);
      } else {
        setApplications([]);
      }
    } catch (err) {
      console.error("Applications fetch error:", err);

      setError(
        err?.response?.data?.message ||
          "Unable to load applications."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const filteredApplications = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return applications.filter((application) => {
      const applicant =
        application?.applicantName ||
        application?.studentName ||
        application?.lead?.name ||
        "";

      const applicationNumber =
        application?.applicationNumber ||
        application?.applicationNo ||
        application?.number ||
        "";

      const email =
        application?.email ||
        application?.lead?.email ||
        "";

      const phone =
        application?.phone ||
        application?.mobile ||
        application?.lead?.phone ||
        "";

      const course =
        application?.course ||
        application?.program ||
        application?.courseName ||
        "";

      const matchesSearch =
        !keyword ||
        String(applicant)
          .toLowerCase()
          .includes(keyword) ||
        String(applicationNumber)
          .toLowerCase()
          .includes(keyword) ||
        String(email)
          .toLowerCase()
          .includes(keyword) ||
        String(phone)
          .toLowerCase()
          .includes(keyword) ||
        String(course)
          .toLowerCase()
          .includes(keyword);

      const status =
        application?.status || "Submitted";

      const matchesStatus =
        statusFilter === "all" ||
        status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  const summary = useMemo(() => {
    return {
      total: applications.length,

      submitted: applications.filter(
        (item) => item?.status === "Submitted"
      ).length,

      review: applications.filter(
        (item) => item?.status === "Under Review"
      ).length,

      approved: applications.filter(
        (item) =>
          item?.status === "Approved" ||
          item?.status === "Admitted"
      ).length,
    };
  }, [applications]);

  const getId = (application) =>
    application?._id || application?.id;

  const getApplicantName = (application) =>
    application?.applicantName ||
    application?.studentName ||
    application?.lead?.name ||
    "Unknown Applicant";

  const getApplicationNumber = (application) =>
    application?.applicationNumber ||
    application?.applicationNo ||
    application?.number ||
    "—";

  const getCourse = (application) =>
    application?.course ||
    application?.program ||
    application?.courseName ||
    "—";

  const getDate = (application) => {
    const value =
      application?.applicationDate ||
      application?.submittedAt ||
      application?.createdAt;

    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString("en-IN");
  };

  const getStatusClass = (status) => {
    switch (String(status).toLowerCase()) {
      case "approved":
      case "admitted":
        return "bg-success-subtle text-success";

      case "rejected":
      case "withdrawn":
        return "bg-danger-subtle text-danger";

      case "under review":
        return "bg-warning-subtle text-warning";

      default:
        return "bg-primary-subtle text-primary";
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <h4 className="mb-1">
              Applications
            </h4>

            <p className="text-muted mb-0">
              Manage student applications and admission
              processing.
            </p>
          </Col>

          <Col
            xs="12"
            md="auto"
            className="mt-3 mt-md-0"
          >
            <Button
              color="light"
              onClick={fetchApplications}
              disabled={loading}
            >
              <FiRefreshCw
                size={15}
                className="me-1"
              />
              Refresh
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* Summary */}
        <Row>
          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiUsers />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Total Applications
                    </p>

                    <h4 className="mb-0">
                      {summary.total}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiUser />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Submitted
                    </p>

                    <h4 className="mb-0">
                      {summary.submitted}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiEdit2 />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Under Review
                    </p>

                    <h4 className="mb-0">
                      {summary.review}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiCheckCircle />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Approved / Admitted
                    </p>

                    <h4 className="mb-0">
                      {summary.approved}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card>
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col lg="6">
                <Label className="form-label">
                  Search
                </Label>

                <div className="position-relative">
                  <FiSearch
                    size={16}
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  />

                  <Input
                    className="ps-5"
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                    placeholder="Search applicant, application number, phone, email..."
                  />
                </div>
              </Col>

              <Col lg="4">
                <Label className="form-label">
                  Status
                </Label>

                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value)
                  }
                >
                  <option value="all">
                    All Statuses
                  </option>

                  {APPLICATION_STATUSES.map(
                    (status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {status}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col lg="2">
                <Button
                  color="light"
                  className="w-100"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                  }}
                >
                  <FiFilter
                    size={15}
                    className="me-1"
                  />
                  Clear
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Applications Table */}
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table
                hover
                className="align-middle mb-0"
              >
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Applicant</th>
                    <th>Application No.</th>
                    <th>Course / Program</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-end">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5"
                      >
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading applications...
                      </td>
                    </tr>
                  ) : filteredApplications.length ===
                    0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5"
                      >
                        <FiUsers
                          size={30}
                          className="text-muted mb-2"
                        />

                        <div className="text-muted">
                          No applications found.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map(
                      (application, index) => {
                        const id =
                          getId(application);

                        const status =
                          application?.status ||
                          "Submitted";

                        return (
                          <tr
                            key={id || index}
                          >
                            <td>
                              {index + 1}
                            </td>

                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-xs">
                                  <div className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                    <FiUser
                                      size={14}
                                    />
                                  </div>
                                </div>

                                <div className="ms-2">
                                  <div className="fw-medium">
                                    {getApplicantName(
                                      application
                                    )}
                                  </div>

                                  {(application?.email ||
                                    application?.lead
                                      ?.email) && (
                                    <div className="text-muted small">
                                      {application?.email ||
                                        application
                                          ?.lead
                                          ?.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="fw-medium">
                                {getApplicationNumber(
                                  application
                                )}
                              </span>
                            </td>

                            <td>
                              {getCourse(
                                application
                              )}
                            </td>

                            <td>
                              {getDate(
                                application
                              )}
                            </td>

                            <td>
                              <Badge
                                className={getStatusClass(
                                  status
                                )}
                              >
                                {status}
                              </Badge>
                            </td>

                            <td>
                              <div className="d-flex justify-content-end">
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/applications/${id}`
                                    )
                                  }
                                  disabled={!id}
                                >
                                  <FiEye
                                    size={14}
                                    className="me-1"
                                  />
                                  View
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Applications;